import type { ReactElement, SVGProps } from "react";

/**
 * Icones inline (sem emoji como UI — RF-UX-092). Todo botao tem nome
 * acessivel; o icone sozinho recebe `label` e vira role="img".
 */
export type IconName =
  | "conversas"
  | "busca"
  | "tarefas"
  | "equipe"
  | "ajustes"
  | "sino"
  | "pin"
  | "more"
  | "back"
  | "send"
  | "attach"
  | "mic"
  | "close"
  | "check"
  | "plus"
  | "chevron"
  | "qr"
  | "filter"
  | "sort"
  | "warning"
  | "user"
  | "label"
  | "note"
  | "mute"
  | "export"
  | "eye"
  | "refresh"
  | "camera"
  | "install"
  | "logout"
  | "reply"
  | "clock"
  | "emoji"
  | "image"
  | "video"
  | "audio"
  | "document"
  | "sticker"
  | "location"
  | "contact"
  | "play"
  | "pause"
  | "trash"
  | "lock";

const PATHS: Record<IconName, ReactElement> = {
  conversas: <path d="M4 5h16v10H8l-4 4V5Z" />,
  busca: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.2-4.2" />
    </>
  ),
  tarefas: <path d="M4 6h16M4 12h16M4 18h10" />,
  equipe: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0M16 6a3 3 0 0 1 0 6M21 20a5 5 0 0 0-4-4.9" />
    </>
  ),
  ajustes: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
    </>
  ),
  sino: <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Zm4 9a2 2 0 0 0 4 0" />,
  pin: <path d="M9 3h6l-1 6 3 3v2H7v-2l3-3-1-6Zm3 11v7" />,
  more: (
    <>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </>
  ),
  back: <path d="m15 5-7 7 7 7" />,
  send: <path d="M4 12 20 4l-6 16-3-7-7-1Z" />,
  attach: <path d="M20 11.5 12 19.5a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 0 1-3-3l7-7" />,
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  check: <path d="m5 13 4 4L19 7" />,
  plus: <path d="M12 5v14M5 12h14" />,
  chevron: <path d="m9 6 6 6-6 6" />,
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM19 19h2v2h-2z" />
    </>
  ),
  filter: <path d="M4 5h16l-6 7v7l-4-2v-5L4 5Z" />,
  sort: <path d="M7 4v16M7 20l-3-3M17 20V4M17 4l3 3" />,
  warning: (
    <>
      <path d="M12 3 2 20h20L12 3Z" />
      <path d="M12 10v5M12 17.5v.5" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  label: <path d="M3 12 12 3h7a2 2 0 0 1 2 2v7l-9 9-9-9Zm14-4h.01" />,
  note: <path d="M5 3h11l4 4v14H5V3Zm3 8h8M8 15h6" />,
  mute: <path d="M4 9v6h4l5 4V5L8 9H4Zm12 0a4 4 0 0 1 0 6M19 6a7 7 0 0 1 0 12" />,
  export: <path d="M12 3v12M8 7l4-4 4 4M5 15v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />,
  eye: (
    <>
      <path d="M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  refresh: <path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v4h-4" />,
  camera: (
    <>
      <path d="M4 8h3l2-2h6l2 2h3v11H4V8Z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  install: <path d="M12 3v11m0 0 4-4m-4 4-4-4M4 21h16" />,
  logout: <path d="M15 4h4v16h-4M11 8 7 12l4 4M7 12h10" />,
  reply: <path d="M9 7 4 12l5 5M5 12h9a5 5 0 0 1 5 5v2" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  emoji: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 10h.01M15 10h.01M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="8.5" cy="9.5" r="1.8" />
      <path d="m4 17 5-5 4 4 3-2.5 4 3.5" />
    </>
  ),
  video: (
    <>
      <rect x="3" y="5" width="13" height="14" rx="2.5" />
      <path d="m16 10 5-3v10l-5-3z" />
    </>
  ),
  audio: (
    <>
      <path d="M9 17V6l9-2v11" />
      <circle cx="6.5" cy="17.5" r="2.5" />
      <circle cx="15.5" cy="15.5" r="2.5" />
    </>
  ),
  document: <path d="M6 3h8l4 4v14H6V3Zm8 0v4h4M9 12h6M9 16h6" />,
  sticker: (
    <>
      <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v7l-5 5H6a2 2 0 0 1-2-2V6Z" />
      <path d="M20 13h-3a2 2 0 0 0-2 2v3M8.5 10h.01M13.5 10h.01M8.5 13a3.5 3.5 0 0 0 5 0" />
    </>
  ),
  location: (
    <>
      <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </>
  ),
  contact: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="10" cy="10.5" r="2.3" />
      <path d="M5.5 17a5 5 0 0 1 9 0M15 9h4M15 13h3" />
    </>
  ),
  play: <path d="M8 5.5v13l11-6.5L8 5.5Z" />,
  pause: (
    <>
      <path d="M9 5v14M15 5v14" strokeWidth="3.2" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 11v6M14 11v6" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </>
  ),
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  size?: number;
  label?: string;
}

export function Icon({ name, size = 20, label, className, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
